import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { UserRole } from '../types';
import { ROLE_DASHBOARD_PATHS } from '../utils/constants';
import { storage } from '../utils/helpers';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Legacy: Allow specific roles */
  allowedRoles?: UserRole[];
  /** New: Require specific permission */
  requiredPermission?: string;
  /** New: Require any of these permissions */
  anyPermission?: string[];
}

export function ProtectedRoute({ children, allowedRoles, requiredPermission, anyPermission }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, userRole } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const location = useLocation();

  // Check if we have a token in localStorage (synchronous check for instant routing)
  const hasToken = !!storage.getToken();

  // If auth is still loading but we have a token, render children optimistically
  // This prevents the redirect flash to /login while auth initializes
  if (isLoading && hasToken) {
    return <>{children}</>;
  }

  // Only redirect to login if auth is complete AND user is not authenticated
  if (!isAuthenticated || !user || !userRole) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permission-based access (new system)
  if (requiredPermission && !hasPermission(requiredPermission)) {
    // Redirect to user's default dashboard if they don't have permission
    const defaultPath = ROLE_DASHBOARD_PATHS[userRole];
    return <Navigate to={defaultPath} replace />;
  }

  // Check any permission access
  if (anyPermission && anyPermission.length > 0 && !hasAnyPermission(anyPermission)) {
    const defaultPath = ROLE_DASHBOARD_PATHS[userRole];
    return <Navigate to={defaultPath} replace />;
  }

  // Check role-based access if allowedRoles is specified (legacy support)
  // SuperAdmin always has access to all routes
  if (allowedRoles && userRole !== UserRole.SUPER_ADMIN && !allowedRoles.includes(userRole)) {
    // Redirect to user's default dashboard if they don't have access
    const defaultPath = ROLE_DASHBOARD_PATHS[userRole];
    return <Navigate to={defaultPath} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
